import cx from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@sb/design-system';

import { CHAT_PANEL_CARD_ID } from './chatPanelConstants';

export type FeedbackSentiment = 'positive' | 'negative';

export interface FeedbackPayload {
  sessionID: string;
  messageID: string;
  sentiment: FeedbackSentiment;
  content?: string;
}

interface MessageFeedbackProps {
  sessionID: string;
  messageID: string;
  /**
   * Fires once the user has both picked a sentiment AND submitted (or
   * skipped) the reason modal. Cancelling the modal clears the
   * pending pick and does not fire the callback — the picked thumb
   * only locks in orange after explicit submission. The mount wires
   * this into the feathers `agent-feedback` create.
   */
  onSubmit: (payload: FeedbackPayload) => void;
}

const ACTIVE_COLOR = 'var(--orange)';
const IDLE_COLOR = 'var(--label-quaternary)';

interface SubmittedFeedback {
  sentiment: FeedbackSentiment;
  reason?: string;
}

/**
 * Resolve the portal target once when the modal opens. The chat card
 * is mounted for the lifetime of every consumer instance of
 * `MessageFeedback`, so a single lookup is sufficient — repeating it
 * on every keystroke (`reason` state updates) is wasteful.
 */
function usePanelPortalTarget(active: boolean): HTMLElement | null {
  return useMemo(() => {
    if (!active) return null;
    if (typeof document === 'undefined') return null;

    return document.getElementById(CHAT_PANEL_CARD_ID);
  }, [active]);
}

/**
 * Render the modal into the chat card via portal when the target is
 * resolved; fall back to inline rendering otherwise so tests and
 * storybook (which don't mount the card) still display the modal.
 */
function renderModalIntoPanel(
  target: HTMLElement | null,
  node: React.ReactNode,
): React.ReactNode {
  if (target === null) return node;

  return createPortal(node, target);
}

/**
 * Thumbs-up / thumbs-down row beneath an assistant message. The
 * follow-up modal is portalled into the chat panel itself
 * (not the viewport) so its backdrop only dims the chat surface;
 * the thumbs stay grey until the user submits the modal so a
 * misclick can be cancelled without recording feedback.
 */
export function MessageFeedback({
  sessionID,
  messageID,
  onSubmit,
}: MessageFeedbackProps) {
  // The thumb is staged when the user clicks it (modal opens) and
  // promoted to `submitted` only after the modal's Submit button
  // fires. Cancelling clears the pending pick.
  const [pending, setPending] = useState<FeedbackSentiment | null>(null);
  const [submitted, setSubmitted] = useState<SubmittedFeedback | null>(null);
  const [reason, setReason] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Resolve the in-panel portal target once per modal-open. Avoids a
  // synchronous `document.getElementById` on every keystroke (every
  // `setReason` re-renders the component).
  const portalTarget = usePanelPortalTarget(pending !== null);

  // Auto-focus the textarea ~50ms after the modal opens so the user
  // can type their reason immediately. The short delay lets the
  // entrance animation start before focus pulls attention.
  useEffect(() => {
    if (pending === null) return undefined;

    const handle = setTimeout(() => textareaRef.current?.focus(), 50);

    return () => clearTimeout(handle);
  }, [pending]);

  // Dismiss the modal on Escape. The backdrop div has no `tabIndex`
  // so it never receives keyboard focus — an `onKeyDown` on the div
  // would never fire. Listen at the window instead while the modal
  // is open. `stopPropagation` keeps the keystroke from also closing
  // the surrounding chat panel.
  useEffect(() => {
    if (pending === null) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setPending(null);
      setReason('');
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pending]);

  const handleThumbClick = useCallback(
    (next: FeedbackSentiment) => {
      // After the user has already submitted feedback for this
      // message, the thumbs are locked — re-clicking does nothing.
      if (submitted !== null) return;
      setPending(next);
      setReason('');
    },
    [submitted],
  );

  const handleCancel = useCallback(() => {
    setPending(null);
    setReason('');
  }, []);

  const handleSubmit = useCallback(() => {
    if (pending === null) return;

    const trimmed = reason.trim();
    const content = trimmed.length > 0 ? trimmed : undefined;

    setSubmitted({ sentiment: pending, reason: content });
    setPending(null);
    setReason('');

    onSubmit({ sessionID, messageID, sentiment: pending, content });
  }, [pending, reason, onSubmit, sessionID, messageID]);

  const renderThumb = (sentiment: FeedbackSentiment) => {
    const isActive = submitted?.sentiment === sentiment;
    const iconKind = sentiment === 'positive' ? 'thumbsUp' : 'thumbsDown';

    const ariaLabel =
      sentiment === 'positive' ? 'Helpful answer' : 'Unhelpful answer';

    return (
      <button
        key={sentiment}
        type="button"
        aria-label={ariaLabel}
        aria-pressed={isActive}
        aria-disabled={submitted !== null}
        title={ariaLabel}
        onClick={() => handleThumbClick(sentiment)}
        className={cx(
          'tw-flex',
          'tw-items-center',
          'tw-justify-center',
          'tw-w-24',
          'tw-h-24',
          'tw-rounded-6',
          'tw-border-none',
          'tw-bg-transparent',
          'tw-p-0',
        )}
        style={{
          // Cursor + color flip with submitted state; transition curve
          // doesn't have a design-system preset.
          cursor: submitted === null ? 'pointer' : 'default',
          color: isActive ? ACTIVE_COLOR : IDLE_COLOR,
          transition: 'color 0.15s ease, background 0.15s ease',
        }}
        onMouseEnter={(event) => {
          if (submitted !== null) return;
          // eslint-disable-next-line no-param-reassign -- imperative hover background paired below
          event.currentTarget.style.background = 'var(--fill-primary)';
        }}
        onMouseLeave={(event) => {
          // eslint-disable-next-line no-param-reassign -- imperative hover background paired below
          event.currentTarget.style.background = 'transparent';
        }}
      >
        <Icon
          kind={iconKind}
          // 14×14 isn't in the icon-size scale.
          style={{ width: 14, height: 14, color: 'currentColor' }}
        />
      </button>
    );
  };

  const isPositive = pending === 'positive';
  const iconKind = isPositive ? 'thumbsUp' : 'thumbsDown';

  const title = isPositive
    ? 'What did you like about this answer?'
    : 'What was wrong with this answer?';

  const placeholder = isPositive
    ? 'Tell us what worked well — accuracy, clarity, the tools we picked…'
    : 'Tell us what we got wrong — missing steps, wrong info, confusing wording…';

  return (
    <>
      <div className="tw-flex tw-items-center tw-gap-4 tw-mt-6">
        {renderThumb('positive')}
        {renderThumb('negative')}
        {submitted?.reason && (
          <span
            className={cx(
              'tw-ml-6',
              'tw-italic',
              'tw-overflow-hidden',
              'tw-text-ellipsis',
              'tw-whitespace-nowrap',
            )}
            style={{
              // 12px font + 40%-black color + 220px max-width aren't in
              // the typography / spacing scales.
              fontSize: 12,
              color: 'var(--label-tertiary)',
              maxWidth: 220,
            }}
            title={submitted.reason}
          >
            “{submitted.reason}”
          </span>
        )}
      </div>

      {pending !== null &&
        renderModalIntoPanel(
          portalTarget,
          <div
            // Modal is portalled into the chat card itself (see
            // `CHAT_PANEL_CARD_ID`) so `position: absolute; inset: 0`
            // aligns to the card regardless of where MessageFeedback
            // is rendered in the DOM (e.g. inside a scrollable +
            // clipped MessageList). Avoids the offset / clipping bugs
            // that surface with `position: fixed` + viewport coords.
            className={cx(
              'tw-absolute',
              'tw-inset-0',
              'tw-flex',
              'tw-items-center',
              'tw-justify-center',
              'tw-p-20',
            )}
            style={{
              // z-index 100 stacks the modal above the panel; backdrop
              // dim color stays inline; animation reuses the chat
              // overlay's keyframe when injected (no-op otherwise).
              zIndex: 100,
              background: 'rgba(0,0,0,0.45)',
              animation: '_chatBackdropIn 0.18s ease forwards',
            }}
            onClick={(e) => {
              // Stop propagation so the click doesn't bubble up
              // through the React portal tree to `ChatPanelOverlay`'s
              // backdrop and dismiss the whole chat panel.
              e.stopPropagation();
              handleCancel();
            }}
            role="presentation"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Feedback"
              className={cx(
                'tw-w-full',
                'tw-max-w-320',
                'tw-rounded-14',
                'tw-p-16',
                'tw-flex',
                'tw-flex-col',
                'tw-gap-12',
              )}
              style={{
                background: 'var(--surface-primary-elevated)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
              }}
            >
              <div className="tw-flex tw-items-center tw-gap-10">
                <div
                  className={cx(
                    'tw-flex',
                    'tw-items-center',
                    'tw-justify-center',
                    'tw-w-32',
                    'tw-h-32',
                    'tw-rounded-full',
                    'tw-shrink-0',
                  )}
                  style={{
                    background: isPositive
                      ? 'rgba(34, 139, 84, 0.12)'
                      : 'rgba(224, 79, 27, 0.12)',
                  }}
                >
                  <Icon
                    kind={iconKind}
                    className="tw-w-16 tw-h-16"
                    style={{
                      color: isPositive ? 'var(--green)' : 'var(--orange)',
                    }}
                  />
                </div>
                <div
                  style={{
                    // 14/600 + `-0.2` letter-spacing aren't in the
                    // typography scale.
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--label-primary)',
                    letterSpacing: -0.2,
                  }}
                >
                  {title}
                </div>
              </div>

              <textarea
                ref={textareaRef}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={placeholder}
                rows={4}
                className={cx(
                  'tw-w-full',
                  'tw-resize-none',
                  'tw-rounded-10',
                  'tw-py-10',
                  'tw-px-12',
                  'tw-text-13',
                  'tw-outline-none',
                )}
                style={{
                  // 1.5 line-height and box-sizing don't map to
                  // design-system tokens; border / text / fill use
                  // themeable tokens. The fill uses `--fill-primary`, not
                  // `--surface-secondary` (which in dark mode matches the
                  // modal card), so the field stays distinct from the card.
                  border: '1px solid var(--divider-primary)',
                  fontFamily: 'inherit',
                  color: 'var(--label-primary)',
                  background: 'var(--fill-primary)',
                  lineHeight: 1.5,
                  boxSizing: 'border-box',
                }}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />

              <div className="tw-flex tw-justify-end tw-gap-8">
                <button
                  type="button"
                  onClick={handleCancel}
                  className={cx(
                    'tw-py-8',
                    'tw-rounded-full',
                    'tw-border-none',
                    'tw-bg-transparent',
                    'tw-cursor-pointer',
                    'tw-text-13',
                    'tw-font-medium',
                  )}
                  style={{
                    // 14px horizontal padding isn't in the spacing scale.
                    paddingLeft: 14,
                    paddingRight: 14,
                    color: 'var(--label-secondary)',
                    fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className={cx(
                    'tw-py-8',
                    'tw-px-16',
                    'tw-rounded-full',
                    'tw-border-none',
                    'tw-cursor-pointer',
                    'tw-text-13',
                    'tw-text-light',
                  )}
                  style={{
                    // 600 weight isn't in the scale (500 → 700). The
                    // orange fill + light label use themeable tokens.
                    background: 'var(--orange)',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                  }}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>,
        )}
    </>
  );
}
